import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { IMG } from "@/lib/data";

const links = [
  { to: "/", label: "Home", testId: NAV.home },
  { to: "/about", label: "About", testId: NAV.about },
  { to: "/herd", label: "Our Herd", testId: NAV.herd },
  { to: "/bulls", label: "Bulls", testId: NAV.bulls },
  { to: "/heifers", label: "Heifers", testId: NAV.heifers },
  { to: "/contact", label: "Contact", testId: NAV.contact },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Routes whose hero is a dark, full-bleed image — the header sits transparent
  // over them and must use the LIGHT (cream) logo + text for contrast.
  const darkHeroRoutes = ["/", "/about", "/bulls"];
  const hasDarkHero = darkHeroRoutes.includes(location.pathname);

  // "light" = white/cream nav content over a dark hero (only before scroll).
  const light = hasDarkHero && !scrolled;
  // header shows glass background whenever it's not floating over a dark hero.
  const solidHeader = scrolled || !hasDarkHero;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let raf;
    const check = () => {
      const y =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        0;
      setScrolled(y > 32);
      raf = requestAnimationFrame(check);
    };
    raf = requestAnimationFrame(check);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: 0.1 }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background,border] duration-500 ${
          solidHeader
            ? "glass-header border-b border-border_subtle"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        {!solidHeader && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-[120px] bg-gradient-to-b from-obsidian/45 to-transparent"
          />
        )}
        <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 flex items-center justify-between h-[68px] md:h-[76px]">
          <Link
            to="/"
            data-testid={NAV.logo}
            className="group flex items-center gap-3.5"
          >
            <img
              src={light ? IMG.markBone : IMG.markInk}
              alt=""
              aria-hidden
              className="h-6 md:h-7 w-auto transition-transform duration-500 group-hover:scale-[1.04]"
            />
            <span
              className={`hidden sm:flex flex-col leading-none border-l pl-3.5 ${
                light ? "border-bone/30" : "border-ink/20"
              }`}
            >
              <span
                className={`font-serif text-[16px] tracking-tight uppercase ${
                  light ? "text-bone" : "text-ink"
                }`}
              >
                Hideaway Cattle Co.
              </span>
              <span
                className={`font-mono text-[10px] tracking-overline uppercase mt-1 ${
                  light ? "text-bone/70" : "text-sage"
                }`}
              >
                Stevensville · Montana
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-[13px] uppercase tracking-overline transition-colors ${
                    light
                      ? isActive
                        ? "text-bone"
                        : "text-bone/70 hover:text-bone"
                      : isActive
                        ? "text-ink"
                        : "text-sage hover:text-ink"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="flex items-center gap-2">
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-dot"
                        className="h-1 w-1 rounded-full bg-terracotta"
                      />
                    )}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-cta-inquire"
              className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 border text-[12px] uppercase tracking-overline transition-colors ${
                light
                  ? "border-bone/60 text-bone hover:bg-bone hover:text-ink"
                  : "border-ink text-ink hover:bg-ink hover:text-bone"
              }`}
            >
              Inquire
              <span aria-hidden>→</span>
            </Link>
            <button
              type="button"
              onClick={() => setOpen((s) => !s)}
              data-testid={NAV.menuToggle}
              aria-label={open ? "Close menu" : "Open menu"}
              className={`lg:hidden inline-flex items-center justify-center h-11 w-11 border transition-colors ${
                light
                  ? "border-bone/70 text-bone hover:bg-bone hover:text-ink"
                  : "border-ink/70 text-ink hover:bg-ink hover:text-bone"
              }`}
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-bone lg:hidden pt-24"
          >
            <div className="px-6 md:px-10">
              <ul className="divide-y divide-border_subtle border-y border-border_subtle">
                {links.map((l, i) => (
                  <motion.li
                    key={l.to}
                    initial={{ y: 24, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      duration: 0.6,
                      delay: 0.08 * i,
                      ease: [0.19, 1, 0.22, 1],
                    }}
                  >
                    <Link
                      to={l.to}
                      data-testid={`mobile-${l.testId}`}
                      className="flex items-baseline justify-between py-6"
                    >
                      <span className="font-serif text-4xl text-ink tracking-tight">
                        {l.label}
                      </span>
                      <span className="font-mono text-[11px] tracking-overline uppercase text-sage">
                        →
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
              <div className="mt-10 font-mono text-[11px] tracking-overline uppercase text-sage">
                Bitterroot Valley · Montana
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
